import React from 'react';
import Picture from '../../components/Picture';
import {ATTRIBUTES, SKILLS, TRAIT_CATEGORIES, CATEGORY_LABELS, localized} from '../data/traits';
import {DISCIPLINES_BY_ID, POWERS_BY_ID, sortDisciplineEntries, sortPowerIds} from '../data/disciplines';
import {CLANS_BY_ID} from '../data/clans';
import {PREDATORS_BY_ID} from '../data/predators';
import {
  getHealthMax,
  getWillpowerMax,
  isImpaired,
  ADVANTAGE_KIND,
  CHARACTER_STATUS,
  valueFromClick,
  cycleDamageBox,
  setHumanity,
  adjustStains,
  adjustXp,
} from '../character';
import {vtmT, currentLanguage} from '../i18n';
import {SectionTitle, Dots, DamageTrack, HumanityTrack, HungerDrops, MiniStepper, formatPool} from './SheetParts';
import '../../styles/vtm/sheet.css';

const TraitColumns = ({traits, values, specialties = [], lng, onTraitClick}) => (
  <div className="vtmTraitColumns">
    {TRAIT_CATEGORIES.map(category => (
      <div key={category} className="vtmTraitColumn">
        <h3 className="vtmCategoryTitle">{localized(CATEGORY_LABELS[category], lng)}</h3>
        {traits.filter(trait => trait.category === category).map(trait => {
          const value = values[trait.id] || 0;
          const traitSpecialties = specialties.filter(item => item.skill === trait.id);
          const name = localized(trait.name, lng);
          const content = (
            <>
              <span className="vtmTraitName">
                {name}
                {traitSpecialties.map(item => (
                  <span key={item.name} className="vtmSpecialty"> · {item.name}</span>
                ))}
              </span>
              <Dots value={value} label={name} />
            </>
          );
          return onTraitClick ? (
            <button key={trait.id} type="button" className={`vtmTraitRow clickable${value === 0 ? ' empty' : ''}`} onClick={() => onTraitClick(trait.id)}>
              {content}
            </button>
          ) : (
            <div key={trait.id} className={`vtmTraitRow${value === 0 ? ' empty' : ''}`}>{content}</div>
          );
        })}
      </div>
    ))}
  </div>
);

const TrackBlock = ({title, detail, children}) => (
  <div className="vtmTrackBlock">
    <div className="vtmTrackHeader">
      <span className="vtmTrackTitle">{title}</span>
      {detail && <span className="vtmTrackDetail">{detail}</span>}
    </div>
    {children}
  </div>
);

const damageDetail = (track, max) => {
  const parts = [];
  if (track.aggravated) parts.push(`${track.aggravated} ${vtmT('aggravated')}`);
  if (track.superficial) parts.push(`${track.superficial} ${vtmT('superficial')}`);
  if (isImpaired(track, max)) parts.push(vtmT('impaired'));
  return parts.join(' · ');
};

/** Un pouvoir se lance s'il a une réserve ou un coût en tests d'exaltation ; les autres sont passifs. */
const isPowerUsable = (power) => Boolean(power.pool || power.rouse);

/** Le nom de la discipline n'est qu'un titre : on lance ses pouvoirs, pas la discipline seule. */
const DisciplineBlock = ({id, discipline, notes, lng, onPowerClick}) => {
  const name = localized(DISCIPLINES_BY_ID[id]?.name, lng) || id;
  return (
    <div className="vtmDiscipline">
      <div className="vtmDisciplineHeader">
        <span className="vtmDisciplineName">{name}</span>
        <Dots value={discipline.level} label={name} />
      </div>
      <ul className="vtmPowers">
        {sortPowerIds(discipline.powers || [], lng).map(powerId => {
          const power = POWERS_BY_ID[powerId];
          if (!power) return <li key={powerId}>{powerId}</li>;
          const note = (notes[powerId] || '').trim();
          const noteId = `vtmPowerNote-${powerId}`;
          const name = localized(power.name, lng);
          const clickable = onPowerClick && isPowerUsable(power);
          return (
            <li
              key={powerId}
              className={`vtmPower${note ? ' hasNote' : ''}`}
              tabIndex={note && !clickable ? 0 : undefined}
              aria-describedby={note && !clickable ? noteId : undefined}
            >
              {clickable ? (
                <button
                  type="button"
                  className="vtmPowerName clickable"
                  onClick={() => onPowerClick(power)}
                  aria-describedby={note ? noteId : undefined}
                >
                  {name}
                </button>
              ) : (
                <span className="vtmPowerName">{name}</span>
              )}
              <span className="vtmPowerMeta">
                {power.rouse ? vtmT('rouseCost', {value: power.rouse}) : vtmT('rouseFree')}
                {power.pool && ` · ${formatPool(power.pool, lng)}`}
              </span>
              {note && <span id={noteId} role="tooltip" className="vtmPowerNote">{note}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const AdvantageList = ({items}) => (
  items.length === 0
    ? <p className="vtmMuted">{vtmT('noneYet')}</p>
    : (
      <ul className="vtmAdvantages">
        {items.map((item, index) => (
          <li key={`${item.name}-${index}`} className="vtmAdvantage">
            <div className="vtmAdvantageHeader">
              <span className="vtmAdvantageName">{item.name}</span>
              <Dots value={item.level} label={item.name} />
            </div>
            {item.description && <p className="vtmAdvantageDescription">{item.description}</p>}
          </li>
        ))}
      </ul>
    )
);

/**
 * @param {Function} [props.onTraitClick] rend les attributs et compétences cliquables (lancer un jet)
 * @param {Function} [props.onPowerClick] (pouvoir) => void, rend les pouvoirs actifs cliquables
 * @param {Function} [props.onSheetChange] (champs modifiés) => void, rend les jauges et l'XP modifiables
 * @param {React.ReactNode} [props.headerExtra] contenu ajouté à côté du statut, sous le nom (ex : bouton Coterie)
 * @param {React.ReactNode} [props.headerTop] lien discret au-dessus du nom (ex : retour à la vue MJ)
 */
const VtmCharacterSheet = ({character, onTraitClick, onPowerClick, onSheetChange, headerExtra, headerTop}) => {
  const lng = currentLanguage();
  const sheet = character.vtm;
  const clan = CLANS_BY_ID[sheet.clan];
  const predator = PREDATORS_BY_ID[sheet.predatorType];
  const healthMax = getHealthMax(sheet);
  const willpowerMax = getWillpowerMax(sheet);
  const advantages = sheet.advantages.filter(item => item.kind === ADVANTAGE_KIND.ADVANTAGE);
  const flaws = sheet.advantages.filter(item => item.kind === ADVANTAGE_KIND.FLAW);
  const xp = {total: sheet.xp?.total || 0, spent: sheet.xp?.spent || 0};
  const xpAvailable = xp.total - xp.spent;
  const editable = Boolean(onSheetChange);
  const edit = (fields) => onSheetChange && onSheetChange(fields);

  return (
    <article className="vtmSheet">
      <div className="vtmSheetHeader">
        <div className="vtmPortrait">
          <Picture character={character} />
        </div>
        <div className="vtmIdentity">
          {headerTop}
          <h1 className="vtmName">{character.name}</h1>
          {(sheet.concept || sheet.desire) && (
            <p className="vtmConcept">
              {sheet.concept}
              {sheet.concept && sheet.desire && ' · '}
              {sheet.desire && `« ${sheet.desire} »`}
            </p>
          )}
          <div className="vtmIdentityRow">
            {/* Un personnage validé est le cas normal en jeu : on n'affiche que les autres statuts */}
            {character.status && character.status !== CHARACTER_STATUS.APPROVED && (
              <span className={`vtmStatus ${character.status}`}>{vtmT(`status_${character.status}`)}</span>
            )}
            {headerExtra}
          </div>
        </div>
        <dl className="vtmFacts">
          <div><dt>{vtmT('clan')}</dt><dd>{localized(clan?.name, lng) || '—'}</dd></div>
          <div><dt>{vtmT('generation')}</dt><dd>{sheet.generation ? vtmT('generationValue', {value: sheet.generation}) : '—'}</dd></div>
          <div><dt>{vtmT('predator')}</dt><dd>{localized(predator?.name, lng) || '—'}</dd></div>
          <div><dt>{vtmT('bloodPotency')}</dt><dd>{sheet.bloodPotency}</dd></div>
        </dl>
      </div>

      <div className="vtmSheetBody">
        <div className="vtmMainColumn">
          <section>
            <SectionTitle>{vtmT('attributes')}</SectionTitle>
            <TraitColumns traits={ATTRIBUTES} values={sheet.attributes} lng={lng} onTraitClick={onTraitClick} />
          </section>
          <section>
            <SectionTitle>{vtmT('skills')}</SectionTitle>
            <TraitColumns traits={SKILLS} values={sheet.skills} specialties={sheet.specialties} lng={lng} onTraitClick={onTraitClick} />
          </section>
          <section>
            <SectionTitle>{vtmT('disciplines')}</SectionTitle>
            {Object.entries(sheet.disciplines).length === 0 && <p className="vtmMuted">{vtmT('noneYet')}</p>}
            <div className="vtmDisciplineGrid">
              {sortDisciplineEntries(sheet.disciplines, lng).map(([id, discipline]) => (
                <DisciplineBlock key={id} id={id} discipline={discipline} notes={sheet.powerNotes || {}} lng={lng} onPowerClick={onPowerClick} />
              ))}
            </div>
          </section>
          <section className="vtmTwoColumns">
            <div>
              <SectionTitle>{vtmT('advantages')}</SectionTitle>
              <AdvantageList items={advantages} />
            </div>
            <div>
              <SectionTitle>{vtmT('flaws')}</SectionTitle>
              <AdvantageList items={flaws} />
            </div>
          </section>
        </div>

        <aside className="vtmSideColumn">
          <TrackBlock title={vtmT('hunger')} detail={`${sheet.hunger} / 5`}>
            <HungerDrops
              hunger={sheet.hunger}
              onDropClick={editable ? (index => edit({hunger: valueFromClick(sheet.hunger, index, 5)})) : undefined}
              dropLabel={index => vtmT('setHunger', {value: valueFromClick(sheet.hunger, index, 5)})}
            />
          </TrackBlock>
          <TrackBlock title={vtmT('health')} detail={damageDetail(sheet.health, healthMax)}>
            <DamageTrack
              max={healthMax}
              track={sheet.health}
              label={vtmT('health')}
              onBoxClick={editable ? (index => edit({health: cycleDamageBox(sheet.health, healthMax, index)})) : undefined}
              boxLabel={index => vtmT('cycleBox', {track: vtmT('health'), value: index + 1})}
            />
          </TrackBlock>
          <TrackBlock title={vtmT('willpower')} detail={damageDetail(sheet.willpower, willpowerMax)}>
            <DamageTrack
              max={willpowerMax}
              track={sheet.willpower}
              label={vtmT('willpower')}
              onBoxClick={editable ? (index => edit({willpower: cycleDamageBox(sheet.willpower, willpowerMax, index)})) : undefined}
              boxLabel={index => vtmT('cycleBox', {track: vtmT('willpower'), value: index + 1})}
            />
          </TrackBlock>
          <TrackBlock title={vtmT('humanity')} detail={`${sheet.humanity}${sheet.stains ? ` · ${sheet.stains} ${vtmT('stains')}` : ''}`}>
            <HumanityTrack
              humanity={sheet.humanity}
              stains={sheet.stains}
              onBoxClick={editable ? (index => edit(setHumanity(valueFromClick(sheet.humanity, index, 10), sheet.stains))) : undefined}
              boxLabel={index => vtmT('setHumanity', {value: valueFromClick(sheet.humanity, index, 10)})}
            />
            {editable && (
              <MiniStepper
                label={vtmT('stainsLabel')}
                value={sheet.stains}
                onChange={delta => edit({stains: adjustStains(sheet.humanity, sheet.stains, delta)})}
                decrementLabel={vtmT('removeStain')}
                incrementLabel={vtmT('addStain')}
              />
            )}
          </TrackBlock>

          {clan && (clan.bane || clan.compulsion) && (
            <section className="vtmClanNotes">
              {clan.bane && <p><strong>{vtmT('bane')}</strong> {localized(clan.bane, lng)}</p>}
              {clan.compulsion && <p><strong>{vtmT('compulsion')}</strong> {localized(clan.compulsion, lng)}</p>}
            </section>
          )}

          <section>
            <SectionTitle>{vtmT('convictions')}</SectionTitle>
            {sheet.convictions.length === 0 && <p className="vtmMuted">{vtmT('noneYet')}</p>}
            {sheet.convictions.map((item, index) => (
              <div key={index} className="vtmConviction">
                <p className="vtmConvictionText">{item.conviction}</p>
                {item.touchstone && <p className="vtmMuted">{vtmT('touchstone')} : {item.touchstone}</p>}
              </div>
            ))}
          </section>

          <section className="vtmMeta">
            {sheet.ambition && <p><strong>{vtmT('ambition')}</strong> {sheet.ambition}</p>}
            {sheet.sire && <p><strong>{vtmT('sire')}</strong> {sheet.sire}</p>}
            <p><strong>{vtmT('experience')}</strong> {vtmT('xpAvailable', {value: xpAvailable})}</p>
            {editable && (
              <div className="vtmXpEditor">
                <MiniStepper
                  label={vtmT('xpEarned')}
                  value={xp.total}
                  onChange={delta => edit({xp: adjustXp(xp, {total: delta})})}
                  decrementLabel={vtmT('xpEarnedMinus')}
                  incrementLabel={vtmT('xpEarnedPlus')}
                />
              </div>
            )}
          </section>
        </aside>
      </div>
    </article>
  );
};

export default VtmCharacterSheet;
